import Vue                  from "vue";
import { mapGetters }       from "vuex";
import { LoadingSpinner }   from "@/components";
import {
    ErrorLogService,
    PartsService }          from "@/services/";
import { PartInfo }         from "@/components/Elements/PartInfo";
import PartInfoCondensed from '@/components/Elements/PartInfoCondensed/PartInfoCondensed.vue';

import { DlTable }          from "@/components/Elements/DlTable";

/**
 * Alternate-part suffix codes used to look up replacement / variant parts.
 * Each key maps to a property on the part object whose value is a Part ID.
 */
const alternatePartCodes = ["EZId", "GPId", "KId", "NId", "OId", "RId", "VId", "XId", "XLId", "YId", "ZId", "ZZId"];

/** Maps single-letter part-type codes to human-readable labels for screen readers. */
const altPartDescriptionMap: Record<string, string> = {
    R:  "Remanufactured",
    XL: "Super XL",
    Z:  "Falcon Hyload",
    GP: "Genuine Part",
    X:  "Falcon Standard",
    EZ: "Superior EZ",
    K:  "Kit",
    V:  "Vulkollan",
    Y:  "Falcon SPS",
};

/**
 * PartDetailRow – Renders a single part's full details (or condensed view)
 * within the traditional list-mode search results. Supports alternate-part
 * tabs that let users switch between variant part numbers (e.g. Remanufactured,
 * Genuine Part, Kit, etc.).
 */
export default Vue.extend({
    name: "PartDetailRow",
    components: {
        PartInfo,
        PartInfoCondensed,
        LoadingSpinner,
        DlTable
    },
    created() {
        this.partInfo = this.partDetail;
        this.HasAltParts();
    },
    computed: {
        ...mapGetters([
            "user", 
            "audienceIds", 
            "activeAccountId",
            "getContentItem"
        ])
    },
    data: () => ({
        /** Local copy of the part detail, updated when alternate tabs are clicked. */
        partInfo: {} as any,
        /** Tab definitions for alternate part variants. */
        altPartTabs: [] as Array<{ title: string; id: string }>,
        isLoadingPartInfo: false,
        hasAltParts: false
    }),
    props: {
        /** The part detail object from the search results. */
        partDetail: {
            type: Object,
            default: () => ({})
        },
        /** When true, renders the condensed PartInfoCondensed view instead of full PartInfo. */
        compactView: {
            type: Boolean,
            default: false
        }
    },
    watch: {
        partDetail() {
            this.partInfo = this.partDetail;
        }
    },
    methods: {
        /**
         * Fetches full part details for a given part ID (used when switching
         * between alternate-part tabs).
         */
        GetNewFullPartDetails(partId: string, partCode: string = "") {
            this.isLoadingPartInfo = true;
            this.partInfo.PartId = partId;
            try {
                PartsService.FullPartDetails(this.activeAccountId, this.user.UserId, partId, this.audienceIds, partCode)
                    .then(results => {
                        this.partInfo.FullPartDetails = results.data;
                    }).catch(err => {
                        ErrorLogService.LogAndGetErrorMessage(err);
                    }).finally(() => {
                        this.isLoadingPartInfo = false;
                    });
            } catch (err) {
                ErrorLogService.LogAndGetErrorMessage(err);
            }
        },
        /** True when only a serial prefix is present (no group number). */
        isSerialPrefixOnly(groupNumber: string, serialPrefix: string): boolean {
            return !!serialPrefix && !groupNumber;
        },
        /** True when only a group number is present (no serial prefix). */
        isGroupNumberOnly(groupNumber: string, serialPrefix: string): boolean {
            return !!groupNumber && !serialPrefix;
        },
        /** True when both group number and serial prefix are present. */
        isGroupAndSerial(groupNumber: string, serialPrefix: string): boolean {
            return !!serialPrefix && !!groupNumber;
        },
        /**
         * Checks whether the current part has any alternate-part IDs and, if
         * so, emits the event and builds the tab list.
         */
        HasAltParts() {
            this.hasAltParts = alternatePartCodes.some(
                code => this.IsNotNullOrUndefined(this.partDetail[code])
            );
            
            if (this.hasAltParts) {
                this.$emit("hasAltParts", this.hasAltParts);
                this.SetUpAltButtonDetails();
            }
        },
        /** Builds the tab array with current part + any alternate variants. */
        SetUpAltButtonDetails() {
            // Start with the current / default part
            this.altPartTabs.push({
                title: this.partInfo.PartCode,
                id: this.partInfo.PartId
            });
            
            // Append all alternate parts found in the data
            alternatePartCodes.forEach(altPartCode => {
                if (this.IsNotNullOrUndefined(this.partDetail[altPartCode])) {
                    this.altPartTabs.push({
                        // Strip trailing 'Id' suffix to derive the display code
                        title: altPartCode.substring(0, altPartCode.length - 2),
                        id: this.partInfo[altPartCode]
                    });
                }
            });
        },
        /** Returns accessible label text describing the alternate part type. */
        SetScreenReaderTextForAltParts(title: string): string {
            const description = altPartDescriptionMap[title];
            if (description) {
                return `Load part detail for ${description} alternative part.`;
            }
            return `Load part detail for part number ${this.partInfo.PartCode}.`;
        }
    },
});
